import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Clock, Edit, Eye, EyeOff, Plus, Trash2, Upload } from 'lucide-react';
import { Navbar } from '../../components/Navbar';
import { Footer } from '../../components/Footer';
import { InvitationPreview } from '../../components/InvitationPreview';
import { assetUrl } from '../../components/design/DesignLayers';
import { PageLoader } from '../../components/Spinner';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { API_URL } from '../../utils/api';
import { loadFontStylesheets } from '../../utils/fonts';
import { formatDate } from '../../utils/format';
import { getErrorMessage } from '../../utils/errors';
import { useDocumentTitle } from '../../hooks/use-document-title';
import { money, StatusPill } from './partnerStatus';

const PILL_INK = 'rounded-full bg-ink text-paper hover:bg-ink/90 font-bold';

// ───────────────────────── apply / waiting ─────────────────────────

const ApplyForm = ({ onApplied, previous }) => {
  const [form, setForm] = useState({
    company_name: previous?.company_name || '',
    website: previous?.website || '',
    contact_name: previous?.contact_name || '',
    phone: previous?.phone || '',
    notes: '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/partner/apply`, form);
      onApplied(data.partner);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not send your application'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 max-w-xl" data-testid="partner-apply-form">
      <div className="grid gap-1.5">
        <Label htmlFor="pa-company">Business name</Label>
        <Input id="pa-company" required maxLength={120} value={form.company_name} onChange={set('company_name')} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="pa-site">Website</Label>
        <Input id="pa-site" type="url" placeholder="https://" maxLength={300} value={form.website} onChange={set('website')} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="pa-contact">Contact name</Label>
          <Input id="pa-contact" maxLength={120} value={form.contact_name} onChange={set('contact_name')} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="pa-phone">Phone</Label>
          <Input id="pa-phone" type="tel" maxLength={40} value={form.phone} onChange={set('phone')} />
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="pa-notes">Tell us about your designs</Label>
        <Textarea id="pa-notes" rows={4} maxLength={2000} value={form.notes} onChange={set('notes')} />
      </div>
      <Button type="submit" disabled={busy} className={`${PILL_INK} h-11 px-6 justify-self-start`}>
        {busy ? 'Sending…' : 'Apply for a partner account'}
      </Button>
    </form>
  );
};

// ───────────────────────── templates ─────────────────────────

const NewTemplateDialog = ({ open, onOpenChange, categories }) => {
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [categoryId, setCategoryId] = useState('');
  const [busy, setBusy] = useState(false);
  const category = categories.find((c) => c.id === categoryId);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API_URL}/partner/templates`, { name: name || 'Untitled template', category_id: categoryId });
      navigate(`/partner/templates/${data.id}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not create the template'));
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>New template</DialogTitle>
          <DialogDescription>Pick the simcha. Its fields are added to the design for you.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="nt-name">Template name</Label>
            <Input id="nt-name" maxLength={120} value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Gold floral" />
          </div>
          <div className="grid gap-1.5">
            <Label>Category</Label>
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger data-testid="nt-category">
                <SelectValue placeholder="Choose a category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c.id} value={c.id}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          {category ? (
            <div className="rounded-xl bg-cream p-3 text-sm">
              <p className="font-bold mb-1">Fields customers will fill in</p>
              {category.required_fields.length ? (
                <ul className="list-disc ps-5 text-[#4A4640]">
                  {category.required_fields.map((f) => (
                    <li key={f.field_input_id}>{f.label}</li>
                  ))}
                </ul>
              ) : (
                <p className="text-[#4A4640]">This category has no required fields.</p>
              )}
            </div>
          ) : null}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={create} disabled={!categoryId || busy} className={PILL_INK}>
            {busy ? 'Creating…' : 'Start designing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const TemplatesTab = ({ categories }) => {
  const [templates, setTemplates] = useState(null);
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    axios
      .get(`${API_URL}/partner/templates`)
      .then(({ data }) => setTemplates(data))
      .catch((err) => {
        setTemplates([]);
        toast.error(getErrorMessage(err, 'Could not load your templates'));
      });
  }, []);
  useEffect(load, [load]);

  const visibility = async (t, active) => {
    try {
      await axios.post(`${API_URL}/partner/templates/${t.id}/visibility`, { active });
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not change visibility'));
    }
  };

  const remove = async () => {
    try {
      await axios.delete(`${API_URL}/partner/templates/${deleting.id}`);
      setDeleting(null);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not delete'));
    }
  };

  const catName = (id) => categories.find((c) => c.id === id)?.name || '';

  return (
    <div>
      <div className="flex justify-end mb-5">
        <Button onClick={() => setCreating(true)} className={`${PILL_INK} h-11 px-5`} data-testid="partner-new-template">
          <Plus className="h-4 w-4" /> New template
        </Button>
      </div>
      {templates === null ? (
        <p className="text-[#6B665C]">Loading…</p>
      ) : templates.length === 0 ? (
        <div className="rounded-2xl bg-white border border-[#E6E1D6] p-10 text-center">
          <p className="font-bold text-lg">No templates yet</p>
          <p className="text-[#4A4640] mt-1">Upload a background, place the text fields, and publish it to Azamein.</p>
        </div>
      ) : (
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          {templates.map((t) => (
            <li key={t.id} className="flex flex-col gap-2">
              <Link to={`/partner/templates/${t.id}`} className="block rounded-lg overflow-hidden border border-[#E6E1D6] bg-white hover:shadow-md transition-shadow">
                {t.background_image ? (
                  <InvitationPreview
                    backgroundImage={assetUrl(t.background_image)}
                    fields={t.text_fields}
                    getText={(f) => f.default_text || ''}
                    alt={t.name}
                  />
                ) : (
                  <div className="aspect-[8.5/11] flex items-center justify-center text-sm text-[#6B665C] bg-cream">No background yet</div>
                )}
              </Link>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-bold text-sm truncate">{t.name}</p>
                  <p className="text-xs text-[#6B665C] truncate">{catName(t.category_id)}</p>
                </div>
                <StatusPill status={t.status} live={t.live_active} />
              </div>
              {t.status === 'rejected' && t.review_note ? <p className="text-xs text-[#A15A00]">{t.review_note}</p> : null}
              {t.live_template_id && t.has_unpublished_changes && t.status !== 'pending' ? (
                <p className="text-xs text-[#6B665C]">Unpublished changes</p>
              ) : null}
              <div className="flex gap-1.5">
                <Button asChild size="sm" variant="outline" className="flex-1">
                  <Link to={`/partner/templates/${t.id}`}><Edit className="h-3.5 w-3.5" /> Edit</Link>
                </Button>
                {t.live_template_id ? (
                  <Button size="sm" variant="outline" onClick={() => visibility(t, !t.live_active)} title={t.live_active ? 'Hide from the catalog' : 'Show in the catalog'}>
                    {t.live_active ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                  </Button>
                ) : null}
                <Button size="sm" variant="outline" onClick={() => setDeleting(t)} aria-label={`Delete ${t.name}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            </li>
          ))}
        </ul>
      )}
      <NewTemplateDialog open={creating} onOpenChange={setCreating} categories={categories} />
      <AlertDialog open={Boolean(deleting)} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.name}?</AlertDialogTitle>
            <AlertDialogDescription>
              {deleting?.live_template_id
                ? 'It will be removed from the Azamein catalog. Customers who already made invitations with it keep them.'
                : 'This draft will be deleted.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove}>Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// ───────────────────────── fonts ─────────────────────────

const FontsTab = () => {
  const [fonts, setFonts] = useState([]);
  const [fontName, setFontName] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    axios.get(`${API_URL}/partner/fonts`).then(({ data }) => {
      setFonts(data);
      loadFontStylesheets(data);
    });
  }, []);
  useEffect(load, [load]);

  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    try {
      await axios.post(`${API_URL}/partner/fonts?font_name=${encodeURIComponent(fontName)}`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFontName('');
      toast.success('Font added');
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (f) => {
    try {
      await axios.delete(`${API_URL}/partner/fonts/${f.id}`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not delete'));
    }
  };

  return (
    <div className="grid gap-6 max-w-3xl">
      <div className="rounded-2xl bg-white border border-[#E6E1D6] p-5 grid gap-3">
        <p className="text-sm text-[#4A4640]">
          Fonts you upload are only available in your own templates. Nobody else on Azamein can use them. TTF, OTF, WOFF or WOFF2, up to 10 MB.
        </p>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="grid gap-1.5 flex-1">
            <Label htmlFor="pf-name">Font name</Label>
            <Input id="pf-name" maxLength={60} value={fontName} onChange={(e) => setFontName(e.target.value)} placeholder="e.g. Studio Script" />
          </div>
          <label className={`${PILL_INK} h-10 px-5 inline-flex items-center gap-2 cursor-pointer text-sm ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
            <Upload className="h-4 w-4" /> {busy ? 'Uploading…' : 'Upload font'}
            <input type="file" accept=".ttf,.otf,.woff,.woff2" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
          </label>
        </div>
      </div>
      {fonts.length ? (
        <ul className="grid gap-2">
          {fonts.map((f) => (
            <li key={f.id} className="rounded-xl bg-white border border-[#E6E1D6] px-4 py-3 flex items-center gap-4">
              <div className="min-w-0 flex-1">
                <p className="text-xs font-bold text-[#6B665C]">{f.name}</p>
                <p className="text-2xl truncate" style={{ fontFamily: `'${f.font_family}'` }} dir="auto">מזל טוב · Mazel Tov</p>
              </div>
              <Button size="sm" variant="outline" onClick={() => remove(f)} aria-label={`Delete ${f.name}`}>
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-[#6B665C]">No fonts yet.</p>
      )}
    </div>
  );
};

// ───────────────────────── earnings ─────────────────────────

const Stat = ({ label, value, hint }) => (
  <div className="rounded-2xl bg-white border border-[#E6E1D6] p-4">
    <p className="text-xs font-bold uppercase tracking-wide text-[#6B665C]">{label}</p>
    <p className="text-2xl font-bold mt-1">{value}</p>
    {hint ? <p className="text-xs text-[#6B665C] mt-0.5">{hint}</p> : null}
  </div>
);

const EarningsTab = ({ partner }) => {
  const [data, setData] = useState(null);
  useEffect(() => {
    axios
      .get(`${API_URL}/partner/earnings`)
      .then(({ data: d }) => setData(d))
      .catch((err) => toast.error(getErrorMessage(err, 'Could not load earnings')));
  }, []);
  if (!data) return <p className="text-[#6B665C]">Loading…</p>;
  const s = data.summary;
  return (
    <div className="grid gap-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Sales" value={s.sales} hint={`${money(s.gross)} total`} />
        <Stat label="Your share" value={money(s.earned)} hint={`Azamein commission ${partner.commission_percent}%`} />
        <Stat label="Paid to you" value={money(s.paid_out)} />
        <Stat label="Balance due" value={money(s.balance)} />
      </div>
      <section>
        <h2 className="font-bold mb-2">Sales</h2>
        {data.sales.length ? (
          <div className="overflow-x-auto rounded-xl border border-[#E6E1D6] bg-white">
            <table className="w-full text-sm">
              <thead className="bg-cream text-left">
                <tr>
                  <th className="px-3 py-2">Date</th>
                  <th className="px-3 py-2">Template</th>
                  <th className="px-3 py-2 text-right">Price</th>
                  <th className="px-3 py-2 text-right">Commission</th>
                  <th className="px-3 py-2 text-right">Your share</th>
                </tr>
              </thead>
              <tbody>
                {data.sales.map((r) => (
                  <tr key={r.id} className="border-t border-[#EFEBE3]">
                    <td className="px-3 py-2">{formatDate(r.sold_at)}</td>
                    <td className="px-3 py-2">{r.template_name}</td>
                    <td className="px-3 py-2 text-right">{money(r.amount)}</td>
                    <td className="px-3 py-2 text-right">{money(r.commission_amount)} ({r.commission_percent}%)</td>
                    <td className="px-3 py-2 text-right font-bold">{money(r.partner_amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-[#6B665C]">No sales yet.</p>
        )}
      </section>
      <section>
        <h2 className="font-bold mb-2">Payments to you</h2>
        {data.payouts.length ? (
          <ul className="grid gap-2">
            {data.payouts.map((p) => (
              <li key={p.id} className="rounded-xl bg-white border border-[#E6E1D6] px-4 py-2.5 flex justify-between gap-3 text-sm">
                <span>
                  {formatDate(p.paid_at)} · {p.method || 'Payment'} {p.reference ? `· ${p.reference}` : ''}
                </span>
                <span className="font-bold">{money(p.amount)}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-[#6B665C]">No payments yet.</p>
        )}
      </section>
    </div>
  );
};

// ───────────────────────── account ─────────────────────────

const LogoCard = ({ partner, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const upload = async (file) => {
    if (!file) return;
    const form = new FormData();
    form.append('file', file);
    setBusy(true);
    try {
      await axios.post(`${API_URL}/partner/logo`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Logo saved');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Upload failed'));
    } finally {
      setBusy(false);
    }
  };
  const remove = async () => {
    try {
      await axios.delete(`${API_URL}/partner/logo`);
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not remove the logo'));
    }
  };
  return (
    <div className="rounded-2xl bg-white border border-[#E6E1D6] p-5 grid gap-3 max-w-xl mb-6" data-testid="partner-logo">
      <div>
        <p className="font-bold">Logo</p>
        <p className="text-sm text-[#4A4640]">
          Shown as a small tag on your templates in the Azamein gallery, where customers can also browse by designer. A PNG with a transparent background looks best.
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <div className="h-14 min-w-[120px] px-3 rounded-xl border border-dashed border-[#E6E1D6] bg-paper inline-flex items-center justify-center">
          {partner.logo_url ? (
            <img src={assetUrl(partner.logo_url)} alt={`${partner.company_name} logo`} className="h-8 w-auto max-w-[220px] object-contain" />
          ) : (
            <span className="text-xs text-[#6B665C]">No logo yet</span>
          )}
        </div>
        <label className={`${PILL_INK} h-10 px-5 inline-flex items-center gap-2 cursor-pointer text-sm ${busy ? 'opacity-50 pointer-events-none' : ''}`}>
          <Upload className="h-4 w-4" /> {busy ? 'Uploading…' : partner.logo_url ? 'Replace logo' : 'Upload logo'}
          <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => upload(e.target.files?.[0])} />
        </label>
        {partner.logo_url ? (
          <Button type="button" size="sm" variant="outline" onClick={remove}>
            Remove
          </Button>
        ) : null}
      </div>
    </div>
  );
};

const AccountTab = ({ partner, onSaved }) => {
  const [form, setForm] = useState({
    company_name: partner.company_name || '',
    website: partner.website || '',
    contact_name: partner.contact_name || '',
    phone: partner.phone || '',
    payout_details: partner.payout_details || '',
  });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.put(`${API_URL}/partner/profile`, form);
      toast.success('Saved');
      onSaved();
    } catch (err) {
      toast.error(getErrorMessage(err, 'Could not save'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
    <LogoCard partner={partner} onSaved={onSaved} />
    <form onSubmit={save} className="grid gap-4 max-w-xl">
      <div className="grid gap-1.5">
        <Label htmlFor="ac-company">Business name</Label>
        <Input id="ac-company" required maxLength={120} value={form.company_name} onChange={set('company_name')} />
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="ac-site">Website</Label>
        <Input id="ac-site" maxLength={300} value={form.website} onChange={set('website')} />
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="grid gap-1.5">
          <Label htmlFor="ac-contact">Contact name</Label>
          <Input id="ac-contact" maxLength={120} value={form.contact_name} onChange={set('contact_name')} />
        </div>
        <div className="grid gap-1.5">
          <Label htmlFor="ac-phone">Phone</Label>
          <Input id="ac-phone" maxLength={40} value={form.phone} onChange={set('phone')} />
        </div>
      </div>
      <div className="grid gap-1.5">
        <Label htmlFor="ac-payout">How should we pay you?</Label>
        <Textarea id="ac-payout" rows={3} maxLength={2000} value={form.payout_details} onChange={set('payout_details')} placeholder="Zelle, check address, or bank details" />
      </div>
      <Button type="submit" disabled={busy} className={`${PILL_INK} h-11 px-6 justify-self-start`}>
        {busy ? 'Saving…' : 'Save'}
      </Button>
    </form>
    </>
  );
};

// ───────────────────────── page ─────────────────────────

export default function PartnerPortal() {
  const [partner, setPartner] = useState(undefined);
  const [categories, setCategories] = useState([]);
  useDocumentTitle('Partner portal');

  const load = useCallback(() => {
    axios
      .get(`${API_URL}/partner/me`)
      .then(({ data }) => setPartner(data.partner))
      .catch(() => setPartner(null));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    if (partner?.status !== 'approved') return;
    axios.get(`${API_URL}/partner/categories`).then(({ data }) => setCategories(data)).catch(() => setCategories([]));
  }, [partner?.status]);

  if (partner === undefined) return <PageLoader label="Loading the partner portal" />;

  let body;
  if (!partner) {
    body = (
      <>
        <p className="text-[#4A4640] mb-6 max-w-2xl">
          Sell your invitation designs through Azamein&rsquo;s phone line, text messages and website. Upload your artwork, place the text, and we handle the orders. You earn on every sale.
        </p>
        <ApplyForm onApplied={setPartner} />
      </>
    );
  } else if (partner.status === 'pending') {
    body = (
      <div className="rounded-2xl bg-white border border-[#E6E1D6] p-8 max-w-xl flex gap-4" data-testid="partner-pending">
        <Clock className="h-6 w-6 shrink-0 text-[#6E5B1C]" />
        <div>
          <p className="font-bold text-lg">Thanks, {partner.company_name}. Your application is in.</p>
          <p className="text-[#4A4640] mt-1">We review every partner account. You can start designing as soon as it&rsquo;s approved.</p>
        </div>
      </div>
    );
  } else if (partner.status === 'rejected') {
    body = (
      <>
        <p className="text-[#4A4640] mb-6 max-w-2xl">Your application wasn&rsquo;t approved. You can update the details and apply again.</p>
        <ApplyForm onApplied={setPartner} previous={partner} />
      </>
    );
  } else if (partner.status === 'suspended') {
    body = <p className="text-[#4A4640]">This partner account is paused. Please contact Azamein.</p>;
  } else {
    body = (
      <Tabs defaultValue="templates">
        <TabsList className="mb-6">
          <TabsTrigger value="templates">Templates</TabsTrigger>
          <TabsTrigger value="fonts">Fonts</TabsTrigger>
          <TabsTrigger value="earnings">Earnings</TabsTrigger>
          <TabsTrigger value="account">Account</TabsTrigger>
        </TabsList>
        <TabsContent value="templates"><TemplatesTab categories={categories} /></TabsContent>
        <TabsContent value="fonts"><FontsTab /></TabsContent>
        <TabsContent value="earnings"><EarningsTab partner={partner} /></TabsContent>
        <TabsContent value="account"><AccountTab partner={partner} onSaved={load} /></TabsContent>
      </Tabs>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-paper">
      <Navbar />
      <main id="main-content" className="flex-1 pt-28 sm:pt-32 pb-20 px-4 sm:px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-[13px] font-bold uppercase tracking-[0.16em] text-[#6E5B1C] mb-2">
            {partner?.status === 'approved' ? partner.company_name : 'For invitation designers'}
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-ink mb-6">
            Partner <span className="italic font-medium">portal</span>
          </h1>
          {body}
        </div>
      </main>
      <Footer />
    </div>
  );
}
